import React from 'react'
import { ThemeProvider as Theme } from 'styled-components'

interface Props {
  theme: Theme
  children: any
}

const ThemeProvider = (props: Props): JSX.Element => {
  return <Theme theme={props.theme}>{props.children}</Theme>
}

export default ThemeProvider

export interface Theme {
  theme: {
    autoComplete?: AutoCompleteTheme
    brandColors?: BrandColorsTheme
    breadcrumbs?: BreadcrumbsTheme
    button?: ButtonTheme
    card?: CardTheme
    checkbox?: CheckboxTheme
    collapse?: CollapseTheme
    hint?: HintTheme
    input?: InputTheme
    inputLabel?: InputLabelTheme
    textareaHint?: TextAreaHintTheme
    navbar?: NavbarTheme
    numericInput?: NumericInputTheme
    paginationTableAlt?: PaginationTableAltTheme
    radioButton?: RadioButtonTheme
    sideMenuAlt?: SideMenuAltTheme
    snackbar?: SnackbarTheme
    switch?: SwitchTheme
    tag?: TagTheme
    table?: TableTheme
    tooltip?: TooltipTheme
    upperMenu?: UpperMenuTheme
    collapseCard?: CollapseCardTheme
    phoneInput?: PhoneInputTheme
    datePicker?: DatePickerTheme
  }
}

interface BrandColorsTheme {
  primaryPure?: string
  primaryLight?: string
  primaryMedium?: string
  primaryDark?: string
  sideMenuDividerColor?: string
  sideMenuFontColor?: string
  sideMenuBackgroundColor?: string
}

interface BreadcrumbsTheme {
  activeBackgroundColor?: string
  activeBorderRadius?: string
  activeColor?: string
  activePadding?: string
  backgroundColor?: string
  borderRadius?: string
  color?: string
  fontSize?: string
  fontWeight?: string
  padding?: string
}

interface SideMenuAltTheme {
  activeBackground?: string
  activeBorderRadius?: string
  activeColor?: string
  activeFontWeight?: string
  announcementBackground?: string
  announcementColor?: string
  announcementTitleColor?: string
  background?: string
  borderColor?: string
  borderWidth?: string
  color?: string
  fontSize?: string
  fontWeight?: string
  height?: string
  iconColor?: string
  iconFontSize?: string
  imageHeight?: string
  imageIconHeight?: string
  imageIconWidth?: string
  imageMargin?: string
  imagePadding?: string
  imageWidth?: string
  notificationBorderColor?: string
  notificationColor?: string
  notificationSize?: string
  paddingBottom?: string
  paddingLeft?: string
  paddingRight?: string
  paddingTop?: string
  sectionColor?: string
  sectionFontSize?: string
  sectionLineHeight?: string
  sectionWeight?: string
  width?: string
}

interface ButtonTheme {
  loadingBorderColor?: string
  loadingBorderRightColor?: string
  borderRadius?: string
  dangerActiveBackgroundColor?: string
  dangerBackgroundColor?: string
  dangerBorder?: string
  dangerColor?: string
  dangerDisabledBackgroundColor?: string
  dangerDisabledColor?: string
  dangerDisabledBorder?: string
  dangerFocusOutline?: string
  dangerFocusShadow?: string
  dangerHoverBackgroundColor?: string
  dangerHoverColor?: string
  dangerHoverShadow?: string
  dangerLoadingBorderColor?: string
  dangerLoadingBorderRightColor?: string
  fontSize?: string
  fontStyle?: string
  fontWeight?: string
  height?: string
  paddingBottom?: string
  paddingLeft?: string
  paddingRight?: string
  paddingTop?: string
  positiveActiveBackgroundColor?: string
  positiveBackgroundColor?: string
  positiveBorder?: string
  positiveColor?: string
  positiveDisabledBackgroundColor?: string
  positiveDisabledBorder?: string
  positiveDisabledColor?: string
  positiveFocusOutline?: string
  positiveFocusShadow?: string
  positiveHoverBackgroundColor?: string
  positiveHoverColor?: string
  positiveHoverShadow?: string
  positiveLoadingBorderColor?: string
  positiveLoadingBorderRightColor?: string
  primaryActiveBackgroundColor?: string
  primaryBackgroundColor?: string
  primaryBorder?: string
  primaryColor?: string
  primaryDisabledBackgroundColor?: string
  primaryDisabledBorder?: string
  primaryDisabledColor?: string
  primaryFocusOutline?: string
  primaryFocusShadow?: string
  primaryHoverBackgroundColor?: string
  primaryHoverColor?: string
  primaryHoverShadow?: string
  pimaryLoadingBorderColor?: string
  pimaryLoadingBorderRightColor?: string
  secondaryActiveBackgroundColor?: string
  secondaryBackgroundColor?: string
  secondaryBorder?: string
  secondaryColor?: string
  secondaryDisabledBackgroundColor?: string
  secondaryDisabledBorder?: string
  secondaryDisabledColor?: string
  secondaryFocusOutline?: string
  secondaryFocusShadow?: string
  secondaryHoverBackgroundColor?: string
  secondaryHoverColor?: string
  secondaryHoverShadow?: string
  secondaryLoadingBorderColor?: string
  secondaryLoadingBorderRightColor?: string
  tertiaryActiveBackgroundColor?: string
  tertiaryBackgroundColor?: string
  tertiaryBorder?: string
  tertiaryColor?: string
  tertiaryDisabledBackgroundColor?: string
  tertiaryDisabledBorder?: string
  tertiaryDisabledColor?: string
  tertiaryFocusOutline?: string
  tertiaryFocusShadow?: string
  tertiaryHoverBackgroundColor?: string
  tertiaryHoverColor?: string
  tertiaryHoverShadow?: string
  tertiaryLoadingBorderColor?: string
  tertiaryLoadingBorderRightColor?: string
}

interface TableTheme {
  borderColor?: string
  borderRadius?: string
  borderStyle?: string
  borderWidth?: string
  color?: string
  fontHeight?: string
  fontSize?: string
  fontWeight?: string
  headerBackground?: string
  headerBorderColor?: string
  headerBorderWidth?: string
  headerColor?: string
  headerFontHeight?: string
  headerFontSize?: string
  headerFontWeight?: string
  headerHeight?: string
  headerTextTransform?: string
  rowBackground?: string
  rowBorderColor?: string
  rowBorderTopColor?: string
  rowBorderWidth?: string
  rowHeight?: string
  rowDisabledColor?: string
  rowHoverBackground?: string
  shadow?: string
}

interface TooltipTheme {
  arrayItemMarginButtom?: string
  arrowBoxShadow?: string
  arrowSize?: string
  backgroundColor?: string
  borderRadius?: string
  boxShadow?: string
  color?: string
  fontSize?: string
  fontWeight?: string
  lineHeight?: string
  margin?: string
  padding?: string
  textAlign?: string
  width?: string
}

interface CheckboxTheme {
  background?: string
  backgroundSelected?: string
  borderColor?: string
  borderColorSelected?: string
  borderRadius?: string
  borderWidth?: string
  marginRight?: string
  selectedColor?: string
}

interface InputTheme {
  background?: string
  borderColor?: string
  borderRadius?: string
  borderWidth?: string
  color?: string
  fontHeight?: string
  fontSize?: string
  iconColor?: string
  iconPadding?: string
  paddingBotton?: string
  paddingLeft?: string
  paddingRight?: string
  paddingTop?: string
  placeholderColor?: string
  prefixColor?: string
  negativeBorderColor?: string
  negativeBackgroundColor?: string
}

interface InputLabelTheme {
  color?: string
  fontSize?: string
  fontWeight?: string
  fontHeight?: string
  marginBottom?: string
}

interface HintTheme {
  color?: string
  fontSize?: string
  fontWeight?: string
  marginBottom?: string
  paddingTop?: string
  marginTop?: string
  negativeColor?: string
}

interface TextAreaHintTheme {
  color?: string
  fontSize?: string
  fontWeight?: string
  marginTop?: string
}

interface AutoCompleteTheme {
  background?: string
  borderColor?: string
  borderRadius?: string
  borderWidth?: string
  color?: string
  disabledBackground?: string
  disabledBorderColor?: string
  fontHeight?: string
  fontSize?: string
  iconColor?: string
  iconPadding?: string
  optionColor?: string
  optionFontSize?: string
  paddingBotton?: string
  paddingLeft?: string
  paddingRight?: string
  paddingTop?: string
  placeholderColor?: string
  prefixColor?: string
}

interface RadioButtonTheme {
  background?: string
  borderColor?: string
  borderWidth?: string
  marginRight?: string
  radioIconSize?: string
  selectedBackground?: string
  size?: string
}

interface SnackbarTheme {
  actionButton: {
    border?: string
    borderRadius?: string
    boxShadow?: string
    fontHeight?: string
    fontSize?: string
    fontWeight?: string
    marginLeft?: string
    padding?: string
  }
  border?: string
  borderRadius?: string
  boxShadow?: string
  closeButtonMarginLeft?: string
  fontSize?: string
  fontWeight?: string
  fontHeight?: string
  neutralColor: {
    highPriority: {
      actionButtonBackgroundColor?: string
      actionButtonColor?: string
      backgroundColor?: string
      borderColor?: string
      color?: string
    }
    lowPriority: {
      actionButtonBackgroundColor?: string
      actionButtonColor?: string
      backgroundColor?: string
      border?: string
      color?: string
    }
  }
  negativeColor: {
    highPriority: {
      actionButtonBackgroundColor?: string
      actionButtonColor?: string
      backgroundColor?: string
      borderColor?: string
      color?: string
    }
    lowPriority: {
      actionButtonBackgroundColor?: string
      actionButtonColor?: string
      backgroundColor?: string
      border?: string
      color?: string
    }
  }
  padding?: string
  positiveColor: {
    highPriority: {
      actionButtonBackgroundColor?: string
      actionButtonColor?: string
      backgroundColor?: string
      borderColor?: string
      color?: string
    }
    lowPriority: {
      actionButtonBackgroundColor?: string
      actionButtonColor?: string
      backgroundColor?: string
      border?: string
      color?: string
    }
  }
  position: {
    bottom?: string
    left?: string
    right?: string
    top?: string
  }
  textMarginLeft?: string
  textMaxWidth?: string
  warningColor: {
    highPriority: {
      actionButtonBackgroundColor?: string
      actionButtonColor?: string
      backgroundColor?: string
      borderColor?: string
      color?: string
    }
    lowPriority: {
      actionButtonBackgroundColor?: string
      actionButtonColor?: string
      backgroundColor?: string
      border?: string
      color?: string
    }
  }
}
interface SwitchTheme {
  backgroundColor?: string
  borderRadius?: string
  checkedBackgroundColor?: string
  disabledBackgroundColor?: string
  disabledBorder?: string
  height?: string
  hoverBackgroundColor?: string
  focusShadow?: string
  sliderBackgroundColor?: string
  sliderDisabledBackgroundColor?: string
  sliderHeight?: string
  sliderShadow?: string
  sliderTransform?: string
  sliderWidth?: string
  width?: string
}

interface NavbarTheme {
  backgroundColor?: string
  border?: string
  marginRight?: string
  marginLeft?: string
  height?: string
  menuItemColor?: string
  menuItemFocusColor?: string
  menuItemFontSize?: string
  menuItemFontWeght?: string
  menuItemHeight?: string
  menuItemHoverBackgroundColor?: string
  menuItemHoverBorderRadius?: string
  menuItemHoverColor?: string
  menuItemIconColor?: string
  menuItemIconHoverColor?: string
  menuItemMargin?: string
  menuItemNotificationColor?: string
  menuItemNotificationLeft?: string
  menuItemNotificationSize?: string
  menuItemNotificationTop?: string
  menuItemPadding?: string
  menuItemPictureSize?: string
  menuItemTextAlign?: string
  menuItemTextFontWeight?: string
  menuItemTextMarginRight?: string
  menuItemWidth?: string
  padding?: string
  subitemColor?: string
  subitemFontHeight?: string
  subitemFontSize?: string
  subitemFontWeight?: string
  subitemHoverColor?: string
  subitemMarginBottom?: string
  submenuBackgoundColor?: string
  submenuBorder?: string
  submenuBorderRadius?: string
  submenuBoxShadow?: string
  submenuPadding?: string
  submenuRight?: string
  submenuTextMarginLeft?: string
  submenuTop?: string
  logoHeight?: string
  logoWidth?: string
}

interface NumericInputTheme {
  borderColor?: string
  borderRadius?: string
  borderWidth?: string
  buttonColor?: string
  displayBorderColor?: string
  displayBorderWidth?: string
  displayWidth?: string
}

interface PaginationTableAltTheme {
  backgroundColor?: string
  border?: string
  borderRadius?: string
  buttonBackgroundColor?: string
  buttonBorder?: string
  buttonBorderRadius?: string
  buttonColor?: string
  buttonDisabledBackgroundColor?: string
  buttonDisabledBorderColor?: string
  buttonDisabledColor?: string
  buttonFocusBackgroundColor?: string
  buttonFocusColor?: string
  buttonFocusShadow?: string
  buttonHoverBackgroundColor?: string
  buttonHoverColor?: string
  buttonPageBackgroundColor?: string
  buttonPageBorder?: string
  buttonPageBorderRadius?: string
  buttonPageColor?: string
  buttonPadding?: string
  fontWeight?: string
  padding?: string
}

interface UpperMenuTheme {
  activeBackgroundColor?: string
  activeBorderBottomColor?: string
  activeColor?: string
  borderBottomColor?: string
  borderBottomWidth?: string
  borderLeftColor?: string
  borderLeftWidth?: string
  borderRadius?: string
  borderRightColor?: string
  borderRightWidth?: string
  borderTopColor?: string
  borderTopWidth?: string
  color?: string
  fontWeight?: string
  hoverBackgroundColor?: string
  hoverColor?: string
  negativeBorderBottomColor?: string
  negativeColor?: string
}

interface TagTheme {
  borderRadius?: string
  fontWeight?: string
  lineHeight?: string
  gap?: string
  fontSize?: string
  padding?: string
  iconSize?: string
  positiveBorderColor?: string
  positiveBackgroundColor?: string
  positiveIconColor?: string
  positiveColor?: string
  negativeBorderColor?: string
  negativeBackgroundColor?: string
  negativeColor?: string
  negativeIconColor?: string
  disabledBorderColor?: string
  disabledBackgroundColor?: string
  disabledColor?: string
  disabledIconColor?: string
  activeBorderColor?: string
  activeBackgroundColor?: string
  activeColor?: string
  activeIconColor?: string
  enabledBorderColor?: string
  enabledBackgroundColor?: string
  enabledColor?: string
  enabledIconColor?: string
}

interface CollapseTheme {
  fontSize?: string
  fontWeight?: string
  borderRadius?: string
  padding?: string
  borderWidth?: string
  openBorderColor?: string
  borderColor?: string
  openBackgroundColor?: string
  backgroundColor?: string
  openColor?: string
  color?: string
  openIconColor?: string
  iconColor?: string
}

interface CardTheme {
  fontSize?: string
  fontWeight?: string
  borderRadius?: string
  borderWidth?: string
  primaryFocusOutline?: string
  borderColor?: string
  color?: string
  iconColor?: string
}

interface CollapseCardTheme {
  border?: string
  borderRadius?: string
  backgroundColor?: string
  titleColor?: string
  descriptionColor?: string
  fontWeightDescription?: string
  fontWeightTitle?: string
  fontSizeTitle?: string
  fontSizeDescription?: string
  width?: string
}

interface PhoneInputTheme {
  borderColor?: string
  borderRadius?: string
  borderWidth?: string
  ddiOptionMarginBottom?: string
  flagBorderRadius?: string
  fontSize?: string
  lineHeight?: string
  marginBottom?: string
  marginTop?: string
  padding?: string
}

interface DatePickerTheme {
  calendarBorderRadius?: string
  calendarBorderColor?: string
  calendarHeaderFontSize?: string
  calendarHeaderFontWeight?: string
  calendarWeekFontSize?: string
  calendarHeaderDayFontSize?: string
  calendarSelectedDayBackgroundColor?: string
  calendarSelectedDayColor?: string
  calendarSelectedDayFontWeight?: string
  calendarCurrentDayColor?: string
  calendarDayFontSize?: string
  borderRadius?: string
  border?: string
  placeholderColor?: string
}
